import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toCsv, downloadCsv } from "@/lib/csv";

type Row = {
  id: string;
  studio_id: string | null;
  start_at: string;
  end_at: string;
  session_token: string | null;
  user_id: string | null;
  outcome: string;
  reason: string | null;
  conflict_source_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

const outcomeMeta: Record<string, { label: string; cls: string }> = {
  acquired: { label: "Acquired", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  blocked_booking: { label: "Conflict: booking", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
  blocked_calendar_block: { label: "Conflict: blocked", cls: "border-orange-500/40 bg-orange-500/10 text-orange-300" },
  blocked_lock: { label: "Conflict: hold", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  error: { label: "Error", cls: "border-red-500/40 bg-red-500/10 text-red-300" },
};

const AdminSlotAttempts = () => {
  const [studios, setStudios] = useState<{ id: string; name: string }[]>([]);
  const [studioId, setStudioId] = useState<string>("all");
  const [outcome, setOutcome] = useState<string>("all");
  const [days, setDays] = useState<string>("7");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("studios").select("id,name").order("display_order").then(({ data }) => setStudios((data ?? []) as any));
  }, []);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - parseInt(days, 10) * 86400000).toISOString();
    let q = supabase.from("slot_attempt_log" as any).select("*").gte("created_at", since).order("created_at", { ascending: false }).limit(500);
    if (studioId !== "all") q = q.eq("studio_id", studioId);
    if (outcome !== "all") q = q.eq("outcome", outcome);
    const { data } = await q;
    setRows((data ?? []) as any);
    setLoading(false);
  };

  useEffect(() => { load(); }, [studioId, outcome, days]);

  const exportCsv = () => {
    if (!rows.length) return;
    const studioName = (id: string | null) =>
      id ? (studios.find((s) => s.id === id)?.name ?? id) : "";
    const flat = rows.map((r) => ({
      created_at: r.created_at,
      outcome: r.outcome,
      reason: r.reason ?? "",
      studio: studioName(r.studio_id),
      studio_id: r.studio_id ?? "",
      start_at: r.start_at,
      end_at: r.end_at,
      session_token: r.session_token ?? "",
      user_id: r.user_id ?? "",
      conflict_source_id: r.conflict_source_id ?? "",
      metadata: JSON.stringify(r.metadata ?? {}),
    }));
    const csv = toCsv(flat);
    downloadCsv(`slot-attempts-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r) => { c[r.outcome] = (c[r.outcome] ?? 0) + 1; });
    return c;
  }, [rows]);

  return (
    <AdminLayout title="SLOT ATTEMPT LOG">
      <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">
        Every slot lock attempt with its outcome. Rows auto-purge after 30 days.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={studioId} onValueChange={setStudioId}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Studio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All studios</SelectItem>
            {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="acquired">Acquired</SelectItem>
            <SelectItem value="blocked_lock">Blocked — hold</SelectItem>
            <SelectItem value="blocked_booking">Blocked — booking</SelectItem>
            <SelectItem value="blocked_calendar_block">Blocked — calendar block</SelectItem>
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Range" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24 hours</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3 h-3 mr-2" />Refresh</Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length}>
          <Download className="w-3 h-3 mr-2" />Export CSV
        </Button>

        <div className="ml-auto flex gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} className={`px-2 py-1 border ${outcomeMeta[k]?.cls ?? "border-border"}`}>{outcomeMeta[k]?.label ?? k}: {v}</span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-panel border border-border p-8 text-center text-sm text-muted-foreground font-body">
          No slot attempts in this window.
        </div>
      ) : (
        <div className="bg-panel border border-border divide-y divide-border">
          {rows.map((r) => {
            const meta = outcomeMeta[r.outcome] ?? { label: r.outcome, cls: "border-border" };
            const studio = studios.find((s) => s.id === r.studio_id)?.name ?? r.studio_id?.slice(0, 8) ?? "—";
            return (
              <div key={r.id} className="p-4 flex flex-wrap items-start gap-4">
                <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${meta.cls}`}>{meta.label}</span>
                <div className="text-xs font-body min-w-[200px]">
                  <div className="font-mono">{new Date(r.start_at).toLocaleString()} → {new Date(r.end_at).toLocaleTimeString()}</div>
                  <div className="text-muted-foreground">{studio}</div>
                </div>
                <div className="text-xs text-muted-foreground flex-1">
                  <div>{r.reason ?? "—"}</div>
                  {r.session_token && <div className="font-mono opacity-60 mt-1">token: {r.session_token}</div>}
                  {r.conflict_source_id && <div className="font-mono opacity-60">conflict: {r.conflict_source_id.slice(0, 8)}…</div>}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                </div>
                {r.studio_id && (
                  <Link
                    to={`/admin/availability/timeline?studio_id=${r.studio_id}&start=${encodeURIComponent(r.start_at)}&end=${encodeURIComponent(r.end_at)}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-primary underline whitespace-nowrap"
                  >
                    Timeline →
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSlotAttempts;